/* =========================================================
   AK sample-order mechanism
   ---------------------------------------------------------
   Single global "Free Flooring Sample" product is added to the
   cart with line-item properties identifying the source floor.
   - Cap: 4 distinct samples per cart
   - Duplicate prevention: same source product can't be added twice
   - Drawer integration: dispatches the standard
     'shopify:cart:lines-update' event (action "add") that the cart
     drawer, cart items and header bubble listen to; the drawer
     auto-opens when it has auto-open. Falls back to /cart.
     Built by hand (no import of @shopify/events): Shopify's asset
     minifier rewrites a dynamic import() into require(), which throws.
   - Visible 'Floor' property (the floor's title) so checkout names each sample.
   ========================================================= */

(function () {
  'use strict';

  var SAMPLE_CAP = 4;
  var MESSAGE_TIMEOUT_MS = 5000;
  var CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update'; // StandardEvents.cartLinesUpdate

  function $$ (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function countSampleLines(cart, sampleVariantId) {
    if (!cart || !cart.items) return { count: 0, sourceIds: [] };
    var count = 0;
    var sourceIds = [];
    cart.items.forEach(function (item) {
      // Recognise a sample line by the property we set, not just variant id —
      // if the customer ever adds the Sample product directly it shouldn't
      // collide with the per-floor cap.
      var props = item.properties || {};
      var sid = props._sample_product_id;
      if (sid) {
        count += 1;
        sourceIds.push(String(sid));
      }
    });
    return { count: count, sourceIds: sourceIds };
  }

  function showMessage(messageEl, html, opts) {
    if (!messageEl) return;
    messageEl.innerHTML = html;
    messageEl.removeAttribute('hidden');
    messageEl.dataset.state = (opts && opts.state) || 'info';

    if (messageEl._dismissTimer) clearTimeout(messageEl._dismissTimer);
    if (opts && opts.autoDismiss) {
      messageEl._dismissTimer = setTimeout(function () {
        messageEl.setAttribute('hidden', '');
        messageEl.innerHTML = '';
      }, MESSAGE_TIMEOUT_MS);
    }
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (!btn.dataset.prevText) btn.dataset.prevText = btn.textContent;
      btn.textContent = 'Adding…';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (btn.dataset.prevText) {
        btn.textContent = btn.dataset.prevText;
        delete btn.dataset.prevText;
      }
    }
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  // Section ids of the cart-items components on the page (the drawer's is
  // "cart-drawer-section"), so /cart/add.js returns their fresh HTML.
  function cartItemsSectionIds() {
    var ids = [];
    $$('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  function notifyCartAdd(cart, sections, variantId) {
    var itemCount = cart.item_count || 0;
    var resolveFn = null;
    var promise = new Promise(function (res) { resolveFn = res; });
    var ev;
    try { ev = new Event(CART_LINES_UPDATE_EVENT, { bubbles: true, composed: true }); } catch (e) { return; }
    ev.action = 'add';
    ev.context = 'product';
    ev.lines = [{ merchandiseId: String(variantId), quantity: 1 }];
    ev.promise = promise;
    document.dispatchEvent(ev);
    resolveFn({
      cart: { totalQuantity: itemCount, id: cart.token || null },
      detail: { items: cart.items, source: 'ak-sample-btn', itemCount: itemCount, sections: sections }
    });
  }

  function addSample(btn) {
    var variantId = btn.getAttribute('data-sample-variant-id');
    var sourceId = btn.getAttribute('data-source-product-id');
    var sourceTitle = btn.getAttribute('data-source-product-title') || '';
    var sourceHandle = btn.getAttribute('data-source-product-handle') || '';
    var sourceSku = btn.getAttribute('data-source-product-sku') || '';
    var messageEl = btn.parentNode.querySelector('[data-ak-sample-message]');

    if (!variantId || !sourceId) {
      showMessage(messageEl, 'Sample isn’t available right now — please contact us.', { state: 'error' });
      return;
    }

    setLoading(btn, true);

    fetchCart()
      .then(function (cart) {
        var info = countSampleLines(cart);

        if (info.count >= SAMPLE_CAP) {
          showMessage(
            messageEl,
            'You’ve reached the ' + SAMPLE_CAP + '-sample limit. <a href="/cart">View cart →</a>',
            { state: 'warn', autoDismiss: true }
          );
          // Leave the button disabled per brief
          btn.classList.remove('is-loading');
          if (btn.dataset.prevText) {
            btn.textContent = btn.dataset.prevText;
            delete btn.dataset.prevText;
          }
          return null;
        }

        if (info.sourceIds.indexOf(String(sourceId)) !== -1) {
          showMessage(
            messageEl,
            'Sample already in cart. <a href="/cart">View cart →</a>',
            { state: 'info', autoDismiss: true }
          );
          setLoading(btn, false);
          return null;
        }

        var sectionIds = cartItemsSectionIds();
        var props = {
          _sample_product_id: sourceId,
          _sample_product_title: sourceTitle,
          _sample_product_handle: sourceHandle,
          _sample_product_sku: sourceSku
        };
        if (sourceTitle) props.Floor = sourceTitle;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: Number(variantId),
              quantity: 1,
              properties: props
            }],
            sections: sectionIds.join(',')
          })
        }).then(function (r) {
          if (!r.ok) {
            return r.json().then(function (err) { throw err; }, function () { throw new Error('add failed'); });
          }
          return r.json();
        }).then(function (added) {
          // Refetch cart to get authoritative item_count, then notify drawer.
          return fetchCart().then(function (newCart) {
            try {
              notifyCartAdd(newCart, (added && added.sections) || {}, variantId);
            } catch (e) { /* fall through to /cart */ }

            // If the drawer isn't present (or auto-open isn't enabled),
            // fall back to /cart so customer sees confirmation.
            var drawer = document.querySelector('cart-drawer-component[auto-open]');
            if (!drawer) {
              window.location.href = '/cart';
              return;
            }
            setLoading(btn, false);
          });
        });
      })
      .catch(function (err) {
        var msg = 'Sample couldn’t be added — please try again or contact us.';
        if (err && err.description) msg = err.description;
        showMessage(messageEl, msg, { state: 'error' });
        setLoading(btn, false);
      });
  }

  // 2026-10-16: one delegated listener (capture phase) instead of per-button
  // wiring, so buttons added after load (floor cards re-rendered by collection
  // filters, search, rails) work too, and a click on a card's sample button
  // never reaches the card's own handlers. ak-boot loads this file on every
  // page and some product sections load it again: bind once.
  if (window.AKSamplesBound) return;
  window.AKSamplesBound = true;
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-ak-sample-btn]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled) return;
    addSample(btn);
  }, true);
})();
